import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import analyticsService from '../../services/analyticsService';

const initialState = {
  dashboard: null,
  selectedPlatform: null,
  selectedConnectionId: null,
  dateRange: {
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end: new Date(),
  },
  isLoading: false,
  error: null,

  // Video metrics
  videos: [],
  videosLoading: false,

  // Trends
  trends: [],
  trendsLoading: false,

  // Platform breakdown
  platformBreakdown: null,
  platformBreakdownLoading: false,

  // Engagement
  engagement: null,
  engagementLoading: false,

  // Top content
  topContent: [],
  topContentLoading: false,

  // Sync state
  syncingPlatforms: {},
  syncingConnections: {},
  lastSyncTimes: JSON.parse(localStorage.getItem('omniconnx_sync_times') || '{}'),

  // Video detail (single video history)
  videoDetail: null,
  videoDetailLoading: false,
};

export const fetchDashboard = createAsyncThunk(
  'analytics/fetchDashboard',
  async ({ startDate, endDate, connectionIds }, { rejectWithValue }) => {
    try {
      return await analyticsService.getDashboard(startDate, endDate, connectionIds);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dashboard');
    }
  }
);

export const fetchVideoMetrics = createAsyncThunk(
  'analytics/fetchVideoMetrics',
  async ({ period = '30d' }, { rejectWithValue }) => {
    try {
      return await analyticsService.getVideoMetrics(period);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch video metrics');
    }
  }
);

export const fetchTrends = createAsyncThunk(
  'analytics/fetchTrends',
  async ({ period = '30d' }, { rejectWithValue }) => {
    try {
      return await analyticsService.getTrends(period);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch trends');
    }
  }
);

export const fetchPlatformBreakdown = createAsyncThunk(
  'analytics/fetchPlatformBreakdown',
  async ({ platform, period = '30d' }, { rejectWithValue }) => {
    try {
      return await analyticsService.getPlatformBreakdown(platform, period);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch platform breakdown');
    }
  }
);

export const fetchEngagement = createAsyncThunk(
  'analytics/fetchEngagement',
  async ({ period = '30d' }, { rejectWithValue }) => {
    try {
      return await analyticsService.getEngagement(period);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch engagement');
    }
  }
);

export const fetchTopContent = createAsyncThunk(
  'analytics/fetchTopContent',
  async ({ limit = 10, period = '30d' }, { rejectWithValue }) => {
    try {
      return await analyticsService.getTopContent(limit, period);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch top content');
    }
  }
);

export const fetchVideoDetail = createAsyncThunk(
  'analytics/fetchVideoDetail',
  async ({ contentId }, { rejectWithValue }) => {
    try {
      return await analyticsService.getVideoMetricHistory(contentId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch video detail');
    }
  }
);

export const syncPlatformMetrics = createAsyncThunk(
  'analytics/syncPlatformMetrics',
  async ({ platform }, { rejectWithValue }) => {
    try {
      await analyticsService.pullMetrics(platform);
      return { platform, syncedAt: new Date().toISOString() };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to sync metrics');
    }
  }
);

export const syncConnectionMetrics = createAsyncThunk(
  'analytics/syncConnectionMetrics',
  async ({ connectionId }, { rejectWithValue }) => {
    try {
      await analyticsService.pullMetricsByConnection(connectionId);
      return { connectionId, syncedAt: new Date().toISOString() };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to sync connection metrics');
    }
  }
);

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setDateRange: (state, action) => {
      state.dateRange = action.payload;
    },
    setSelectedPlatform: (state, action) => {
      state.selectedPlatform = action.payload;
    },
    setSelectedConnectionId: (state, action) => {
      state.selectedConnectionId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearPlatformBreakdown: (state) => {
      state.platformBreakdown = null;
    },
    clearVideoDetail: (state) => {
      state.videoDetail = null;
    },
    initSyncTimes: (state) => {
      state.lastSyncTimes = JSON.parse(localStorage.getItem('omniconnx_sync_times') || '{}');
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard
      .addCase(fetchDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Video metrics
      .addCase(fetchVideoMetrics.pending, (state) => { state.videosLoading = true; })
      .addCase(fetchVideoMetrics.fulfilled, (state, action) => {
        state.videosLoading = false;
        state.videos = action.payload || [];
      })
      .addCase(fetchVideoMetrics.rejected, (state) => { state.videosLoading = false; })

      // Trends
      .addCase(fetchTrends.pending, (state) => { state.trendsLoading = true; })
      .addCase(fetchTrends.fulfilled, (state, action) => {
        state.trendsLoading = false;
        state.trends = action.payload || [];
      })
      .addCase(fetchTrends.rejected, (state) => { state.trendsLoading = false; })

      // Platform breakdown
      .addCase(fetchPlatformBreakdown.pending, (state) => { state.platformBreakdownLoading = true; })
      .addCase(fetchPlatformBreakdown.fulfilled, (state, action) => {
        state.platformBreakdownLoading = false;
        state.platformBreakdown = action.payload;
      })
      .addCase(fetchPlatformBreakdown.rejected, (state) => { state.platformBreakdownLoading = false; })

      // Engagement
      .addCase(fetchEngagement.pending, (state) => { state.engagementLoading = true; })
      .addCase(fetchEngagement.fulfilled, (state, action) => {
        state.engagementLoading = false;
        state.engagement = action.payload;
      })
      .addCase(fetchEngagement.rejected, (state) => { state.engagementLoading = false; })

      // Top content
      .addCase(fetchTopContent.pending, (state) => { state.topContentLoading = true; })
      .addCase(fetchTopContent.fulfilled, (state, action) => {
        state.topContentLoading = false;
        state.topContent = action.payload || [];
      })
      .addCase(fetchTopContent.rejected, (state) => { state.topContentLoading = false; })

      // Video detail
      .addCase(fetchVideoDetail.pending, (state) => { state.videoDetailLoading = true; })
      .addCase(fetchVideoDetail.fulfilled, (state, action) => {
        state.videoDetailLoading = false;
        state.videoDetail = action.payload || [];
      })
      .addCase(fetchVideoDetail.rejected, (state) => { state.videoDetailLoading = false; })

      // Sync platform
      .addCase(syncPlatformMetrics.pending, (state, action) => {
        state.syncingPlatforms[action.meta.arg.platform] = true;
      })
      .addCase(syncPlatformMetrics.fulfilled, (state, action) => {
        const { platform, syncedAt } = action.payload;
        state.syncingPlatforms[platform] = false;
        state.lastSyncTimes[platform] = syncedAt;
        localStorage.setItem('omniconnx_sync_times', JSON.stringify(state.lastSyncTimes));
      })
      .addCase(syncPlatformMetrics.rejected, (state, action) => {
        state.syncingPlatforms[action.meta.arg.platform] = false;
      })

      // Sync connection
      .addCase(syncConnectionMetrics.pending, (state, action) => {
        state.syncingConnections[action.meta.arg.connectionId] = true;
      })
      .addCase(syncConnectionMetrics.fulfilled, (state, action) => {
        const { connectionId, syncedAt } = action.payload;
        state.syncingConnections[connectionId] = false;
        state.lastSyncTimes[connectionId] = syncedAt;
        localStorage.setItem('omniconnx_sync_times', JSON.stringify(state.lastSyncTimes));
      })
      .addCase(syncConnectionMetrics.rejected, (state, action) => {
        state.syncingConnections[action.meta.arg.connectionId] = false;
      });
  },
});

export const {
  setDateRange,
  setSelectedPlatform,
  setSelectedConnectionId,
  clearError,
  clearPlatformBreakdown,
  clearVideoDetail,
  initSyncTimes,
} = analyticsSlice.actions;
export default analyticsSlice.reducer;
